/* collection-lazy-cards.js — Phase A lazy card hydration.
 *
 * The collection page renders the first chunk of products as full cards and the
 * rest as lightweight STUBS that still carry every data-* filter attribute, so
 * the client-side filter engine (my-filter-coordinator + horizontal-filter-bar)
 * sees all products from first paint. This script swaps each stub's inner markup
 * for the real card on demand:
 *   - on scroll (stub nears the viewport), and
 *   - when a filter reflows a matching stub into view.
 * Both are handled by one IntersectionObserver: a hidden (display:none) stub
 * never intersects, so filtered-out products are never fetched.
 *
 * Full cards are fetched chrome-free from <collection.url>?view=cards&page=N
 * (templates/collection.cards.liquid -> sections/cards-chunk.liquid), one fetch
 * per chunk, cached. Hydration is IN PLACE on the same outer element, so the
 * FilterManager's element references stay valid.
 */
(function () {
  'use strict';

  var grid = document.querySelector('[data-productgrid-items]');
  if (!grid || !('IntersectionObserver' in window)) return;
  if (!grid.querySelector('[data-stub]')) return; // nothing deferred

  // Preserve the active server-side sort so chunk N here matches the stub order.
  var search = new URLSearchParams(window.location.search);
  var sortBy = search.get('sort_by');

  function chunkUrl(page) {
    var p = new URLSearchParams();
    p.set('view', 'cards');
    p.set('page', page);
    if (sortBy) p.set('sort_by', sortBy);
    return window.location.pathname + '?' + p.toString();
  }

  var pageCache = {}; // page -> Promise<NodeList|null>

  function fetchChunk(page) {
    if (pageCache[page]) return pageCache[page];
    pageCache[page] = fetch(chunkUrl(page), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelectorAll('[data-product-item][data-product-id]');
      })
      .catch(function () { pageCache[page] = null; return null; });
    return pageCache[page];
  }

  function cssEscape(v) {
    return (window.CSS && CSS.escape) ? CSS.escape(v) : String(v).replace(/"/g, '\\"');
  }

  function hydrateChunk(page) {
    return fetchChunk(page).then(function (cards) {
      if (!cards || !cards.length) return;
      var hydratedAny = false;
      cards.forEach(function (card) {
        var id = card.getAttribute('data-product-id');
        var stub = grid.querySelector('[data-stub][data-product-id="' + cssEscape(id) + '"]');
        if (!stub) return;
        stub.innerHTML = card.innerHTML;   // in place: same outer element kept
        stub.removeAttribute('data-stub');
        io.unobserve(stub);
        hydratedAny = true;
      });
      if (hydratedAny) {
        // Let lazy-image watcher pick up the new <img>s, and let the filter
        // coordinator re-sync counts. Harmless if no listeners are attached.
        document.dispatchEvent(new CustomEvent('theme:rimg:watch'));
        document.dispatchEvent(new CustomEvent('productsLoaded'));
      }
    });
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var page = entry.target.getAttribute('data-page');
      if (page) hydrateChunk(page);
    });
  }, { root: null, rootMargin: '800px 0px', threshold: 0 });

  function observeStubs() {
    grid.querySelectorAll('[data-stub]').forEach(function (stub) { io.observe(stub); });
  }

  observeStubs();

  // If new stubs ever get appended (e.g. a >250-product collection's fallback
  // "load all"), observe them too.
  document.addEventListener('productsLoaded', function () {
    grid.querySelectorAll('[data-stub]').forEach(function (stub) { io.observe(stub); });
  });
})();
